<template>
  <div class="content">
    <h1 class="has-text-centered">Akku Ladezeit berechnen</h1>

    <div class="has-text-centered" v-if="calculators.length > 1">
      <label class="label">Welche Formel möchtest du benutzen?</label>
      <div class="field pb-2">
        <div class="dropdown is-300px-width" :class="{ 'is-active': dropdown_active }">
          <div class="dropdown-trigger is-300px-width">
            <button class="button is-fullwidth" @click="toggleDropdown()">
              <span v-if="target_calculator.title != ''">{{ target_calculator.title }}</span>
              <span v-else v-katex="target_calculator.katex_formula"></span>
              <span class="icon is-small">
                <i class="fas fa-angle-down" aria-hidden="true"></i>
              </span>
            </button>
          </div>
          <div class="dropdown-menu is-300px-width">
            <div class="dropdown-content">
              <div class="dropdown-item is-clickable" v-for="(calculator, index) in calculators" :key="index" @click="selectTargetCalculator(index)" v-show="index != target_calculator_index">
                <p v-if="calculator.title != ''">{{ calculator.title }}</p>
                <p v-else v-katex="calculator.katex_formula"></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DefaultCalculator v-for="(calculator, index) in calculators" :key="index" :prop_katex_formula="calculator.katex_formula" :prop_parser_formula="calculator.parser_formula" :prop_variables="calculator.variables" :prop_id="calculator.id" :prop_target_calculator_index="target_calculator_index" :prop_target_variable="getTargetVariable()" v-show="target_calculator_index == index" />

    <FeedbackBox />

    <GeneralAffiliateBox />

    <div class="box">
      <h2>Das Aufladen von Akkus</h2>
      <p>
        Das Auf- und Entladen von Akkumulatoren erzeugt Wärme wodurch ein Teil der Energie verloren geht.
        <br />
        Beim Aufladen ist das Verhältnis von aufgenommener (entnehmbarer) Ladung zu zugeführter Ladung der Ladewirkungsgrad <span v-katex="'\\eta=\\frac{Q_{ab}}{Q_{zu}}'"></span>.
        <br />
        Dieser Wirkungsgrad liegt üblicherweise in einem Bereich von 70% bis 85%
        <br />
        Meistens wird der Kehrwert des Ladewirkungsgrad angeben: der Ladefaktor
        <span v-katex="'\\kappa=\\frac{1}{\\eta}'"></span>
      </p>

      <h2>Wie berechnet man die Ladezeit eines Akkus?</h2>
      <p>
        Mit Hilfe des Ladefaktors &kappa;, des Ladestroms und der maximalen Ladungsmenge Q des Akkus lässt sich die ungefähre
        <b>Ladezeit t</b>
        eines Akkus bestimmen:
      </p>

      <FormulaDescription v-for="(calculator, index) in calculators" :key="index" :katex_formula="calculator.katex_formula" :variables="calculator.variables" />

      <h2>Weiterführende Links</h2>
      <p>
        <ul class="fa-ul pb-3">
          <li>
            <span class="fa-li"><i class="fas fa-arrow-circle-right"></i></span>
            <a href="https://de.wikipedia.org/wiki/Akkumulator#Ladezeiten" target="_blank">Wikipedia-Artikel</a>
          </li>
        </ul>
      </p>

    </div>
  </div>
</template>

<script>
import DefaultCalculator from '@/components/DefaultCalculator.vue'
import FormulaDescription from '@/components/FormulaDescription.vue'
import FeedbackBox from '@/components/FeedbackBox.vue'
import GeneralAffiliateBox from '@/components/GeneralAffiliateBox.vue'

export default {
  name: 'calculator-battery-charging-time',
  components: {
    DefaultCalculator,
    FormulaDescription,
    FeedbackBox,
    GeneralAffiliateBox
  },
  metaInfo: {
    title: 'Ladezeit berechnen',
    meta: [
      {
        name: 'description',
        content: 'Mit diesem Rechner kannst du die Ladezeit eines Akkus berechnen ✓ Zeit ✓ Nennladung ✓ Stromstärke ✓ Ladewirkungsgrad'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'calculators' })
    this.target_calculator_index = this.getTargetCalculator()
  },
  methods: {
    toggleDropdown () {
      if (this.dropdown_active) {
        this.dropdown_active = false
      } else {
        this.dropdown_active = true
      }
    },
    selectTargetCalculator (index) {
      this.target_calculator_index = index
      this.dropdown_active = false
    },
    getValue (calcId, variable) {
      variable = variable.toLowerCase() + '_val'
      const reg = /^[+-]?[0-9]{1,16}(?:\.[0-9]{1,16})?$/
      if (parseInt(this.$route.query.calc, 10) === calcId) {
        for (const key in this.$route.query) {
          if (key === variable && reg.test(this.$route.query[key])) {
            return this.$route.query[key]
          }
        }
      }
      return 1
    },
    getMultiplicator (calcId, variable) {
      variable = variable.toLowerCase() + '_mul'
      const reg = /^[+-]?[0-9]{1,16}(?:\.[0-9]{1,16})?$/
      if (parseInt(this.$route.query.calc, 10) === calcId) {
        for (const key in this.$route.query) {
          if (key === variable && reg.test(this.$route.query[key])) {
            return Math.pow(10, this.$route.query[key])
          }
        }
      }
      return 1
    },
    getTargetVariable () {
      for (const i in this.calculators) {
        if (parseInt(this.$route.query.calc, 10) === this.calculators[i].id) {
          for (const key in this.$route.query) {
            if (key === 'target_var') {
              for (const y in this.calculators[i].variables) {
                if (this.calculators[i].variables[y].variable.toLowerCase() === this.$route.query[key]) {
                  return this.calculators[i].variables[y].label
                }
              }
            }
          }
        }
      }
      return null
    },
    getTargetCalculator () {
      for (const i in this.calculators) {
        if (parseInt(this.$route.query.calc, 10) === this.calculators[i].id) {
          return parseInt(i)
        }
      }
      return 0
    }
  },
  computed: {
    target_calculator: function () {
      return this.calculators[this.target_calculator_index]
    }
  },
  data () {
    return {
      dropdown_active: false,
      target_calculator_index: 0,
      calculators: [
        {
          id: 1,
          title: '',
          katex_formula: 't = \\frac{Q}{I}*\\kappa',
          parser_formula: 't-Q/I*k',
          variables: [
            {
              variable: 't',
              unit: {
                content: 's',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 't',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Zeit in Sekunden [s]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 't'),
                unitMultiplicator: this.getMultiplicator(1, 't')
              },
              is_constant: false
            },
            {
              variable: 'Q',
              unit: {
                content: 'Ah',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'Q_{max}',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Maximale Ladungsmenge in Amperestunden [Ah]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'Q'),
                unitMultiplicator: this.getMultiplicator(1, 'Q')
              },
              is_constant: false
            },
            {
              variable: 'I',
              unit: {
                content: 'A',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'I',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Ladestrom in Ampere [A]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'I'),
                unitMultiplicator: this.getMultiplicator(1, 'I')
              },
              is_constant: false
            },
            {
              variable: 'kappa',
              unit: {
                content: '',
                type: 'katex',
                has_prefix: false
              },
              label: {
                short: [
                  {
                    content: '\\kappa',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Ladefaktor',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'kappa'),
                unitMultiplicator: this.getMultiplicator(1, 'kappa')
              },
              is_constant: false
            }
          ]
        }
      ]
    }
  }
}
</script>
